// The pure dance-visuals overlay model (dance-visuals; client-rendering "Dance drawables").
// Mirrors the `corpse.ts` seam: pure and Pixi-free — the renderer feeds it the confirmed
// close-tier `WorldSnapshot` (dances + bees + danceRadius) once per accepted close frame and
// reads world-space drawables to project into the y-sorted draw list. The GPU paint itself
// stays device-exercised only (repo convention D-086).
//
// Three overlays (UI.md §8): a loud **dancing indicator** over each dancer, a **direction
// arrow** from the dancer toward the dequantized target (a fixed readable length, not scaled
// to distance — a to-scale arrow to a far patch would leave the viewport), hued by
// `tagHash`; and a quieter **watcher cue** over each close-tier bee within `danceRadius` of an
// active dance. A dance renders ONLY when its `dancerId` resolves to a live bee (Architecture
// review A1) — an origin-less dance is skipped, never drawn at a fallback point. The watcher
// set is derived once per accepted close frame (~10 Hz), not per RAF (Architecture review A2),
// so the O(dances × bees) proximity work stays off the 60 Hz render path.

/** A close-tier dance the renderer feeds in: dancer id, dequantized target (world units),
 * and the cosmetic tag hue key. The dancer's own position is resolved from `bees`, not here. */
export interface DanceInput {
  readonly dancerId: number;
  readonly target: readonly [number, number];
  readonly tagHash: number;
}

/** The minimal live-bee projection the tracker needs: its world position (the arrow origin
 * and the watcher-proximity centre). */
export interface DanceBee {
  readonly position: readonly [number, number];
}

/** The confirmed close-tier world the tracker reads once per accepted close frame. */
export interface DanceWorld {
  readonly dances: ReadonlyMap<number, DanceInput>;
  readonly bees: ReadonlyMap<number, DanceBee>;
  /** The match dance audibility radius (world units, from the manifest). */
  readonly danceRadius: number;
}

/** The one monotonic discontinuity counter the tracker resets on (reconnect / tier change) —
 * mirrors `CorpseEpochs`, a structural subset so a full `RenderInput` can be passed directly. */
export interface DanceEpochs {
  readonly connectionEpoch: number;
}

/** The three render modes over the two wire tiers (UI.md §8): `full` (close, nominal —
 * animated indicator + arrow + watcher cue), `reduced` (close, degraded — static arrow +
 * static indicator, watcher cue dropped), `none` (wide — no dance visual at all). */
export type DanceRenderMode = 'full' | 'reduced' | 'none';

/** A world-space direction arrow anchored at the dancer, pointing toward the target. The
 * renderer draws it at {@link ARROW_LENGTH} screen pixels along `angle`, hued by `tagHash`. */
export interface DanceArrowDrawable {
  readonly kind: 'dance-arrow';
  readonly dancerId: number;
  readonly origin: readonly [number, number];
  /** Direction toward the target, radians, world frame (atan2(dy, dx)). */
  readonly angle: number;
  readonly tagHash: number;
}

/** The loud dancing indicator over a dancer. `animated` is true in `full` mode (pulsing
 * waggle glyph) and false in `reduced` (static). */
export interface DanceIndicatorDrawable {
  readonly kind: 'dance-indicator';
  readonly dancerId: number;
  readonly origin: readonly [number, number];
  readonly animated: boolean;
}

/** The quiet watcher cue over a bee within the dance radius (emitted in `full` mode only). */
export interface DanceWatcherDrawable {
  readonly kind: 'dance-watcher';
  readonly beeId: number;
  readonly position: readonly [number, number];
}

export type DanceDrawable = DanceArrowDrawable | DanceIndicatorDrawable | DanceWatcherDrawable;

/** The fixed on-screen arrow length in pixels — a readable direction cue, deliberately not
 * scaled to target distance (dance-signals.html sketch, plan Decision 7). */
export const ARROW_LENGTH = 46;

/** Squared Euclidean distance between two world points. */
function distSq(a: readonly [number, number], b: readonly [number, number]): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  return dx * dx + dy * dy;
}

/**
 * Derives the watcher set — the ids of close-tier bees within `danceRadius` of any active
 * dance's dancer (excluding the dancers themselves). Pure and O(dances × bees); the caller
 * runs it once per accepted close frame (not per RAF), per Architecture review A2. A dance
 * whose `dancerId` does not resolve to a live bee contributes no centre (A1). Documented
 * cosmetic imprecision (design): the close tier carries no per-bee colony id, so this cannot
 * filter to family — any bee within the radius is a watcher, which may include an enemy.
 */
export function deriveWatchers(world: DanceWorld): Set<number> {
  const watchers = new Set<number>();
  const radiusSq = world.danceRadius * world.danceRadius;
  // Resolve the active dancers' positions (skip origin-less dances — A1).
  const dancerPositions: { id: number; position: readonly [number, number] }[] = [];
  for (const dance of world.dances.values()) {
    const dancer = world.bees.get(dance.dancerId);
    if (dancer) dancerPositions.push({ id: dance.dancerId, position: dancer.position });
  }
  if (dancerPositions.length === 0) return watchers;
  for (const [beeId, bee] of world.bees) {
    for (const dancer of dancerPositions) {
      if (dancer.id === beeId) continue; // the dancer does not watch itself
      if (distSq(bee.position, dancer.position) <= radiusSq) {
        watchers.add(beeId);
        break;
      }
    }
  }
  return watchers;
}

/**
 * Builds the dance drawables for one frame from the confirmed world, a pre-derived watcher
 * set, and the render mode. Pure: a dance renders only when its `dancerId` resolves to a live
 * bee (A1); `full` emits animated indicator + arrow + watcher cues; `reduced` emits a static
 * indicator + arrow and drops the watcher cue; `none` emits nothing.
 */
export function danceDrawables(
  world: DanceWorld,
  watchers: ReadonlySet<number>,
  mode: DanceRenderMode,
): DanceDrawable[] {
  if (mode === 'none') return [];
  const out: DanceDrawable[] = [];
  const animated = mode === 'full';
  // Deterministic order: ascending dancer id, so the draw list is stable.
  const dances = [...world.dances.values()].sort((a, b) => a.dancerId - b.dancerId);
  for (const dance of dances) {
    const dancer = world.bees.get(dance.dancerId);
    if (!dancer) continue; // A1: origin-less dance skipped, never drawn at a fallback point
    const origin = dancer.position;
    const angle = Math.atan2(dance.target[1] - origin[1], dance.target[0] - origin[0]);
    out.push({ kind: 'dance-indicator', dancerId: dance.dancerId, origin, animated });
    out.push({ kind: 'dance-arrow', dancerId: dance.dancerId, origin, angle, tagHash: dance.tagHash });
  }
  if (mode === 'full') {
    const ids = [...watchers].sort((a, b) => a - b);
    for (const beeId of ids) {
      const bee = world.bees.get(beeId);
      if (bee) out.push({ kind: 'dance-watcher', beeId, position: bee.position });
    }
  }
  return out;
}

/**
 * Stateful tracker mirroring `CorpseTracker`: the renderer calls {@link onCloseFrame} once per
 * accepted close frame (recomputing + caching the watcher set), and {@link drawables} every RAF
 * (reusing the cached watchers — no per-RAF proximity work, Architecture review A2). A
 * `connectionEpoch` change (reconnect / tier change) clears the cache so no stale watcher set
 * carries across an identity discontinuity (mirrors `CorpseTracker`).
 */
export class DanceTracker {
  private watchers: Set<number> = new Set();
  private lastConnectionEpoch = -1;
  private initializedEpoch = false;

  /** Consume one accepted close frame: reset on an epoch change, then recompute + cache the
   * watcher set from this frame's dances/bees. */
  onCloseFrame(world: DanceWorld, epochs: DanceEpochs): void {
    if (!this.initializedEpoch) {
      this.initializedEpoch = true;
      this.lastConnectionEpoch = epochs.connectionEpoch;
    } else if (epochs.connectionEpoch !== this.lastConnectionEpoch) {
      this.clear();
      this.lastConnectionEpoch = epochs.connectionEpoch;
    }
    this.watchers = deriveWatchers(world);
  }

  /** The dance drawables for this RAF frame, using the watcher set cached at the last accepted
   * close frame (never recomputing proximity here). */
  drawables(world: DanceWorld, mode: DanceRenderMode): DanceDrawable[] {
    return danceDrawables(world, this.watchers, mode);
  }

  /** The cached watcher id set (from the last accepted close frame). */
  get watcherIds(): ReadonlySet<number> {
    return this.watchers;
  }

  /** Clears the cached watcher set (a tier change to wide, in addition to the internal
   * reconnect reset `onCloseFrame` performs). */
  clear(): void {
    this.watchers = new Set();
  }
}
